import {useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {useQuery} from '@apollo/client/react';
import {ExternalLink} from 'lucide-react';
import {ActiveSubscription, SeatUsage} from '../types/graphql';
import {StatsCard} from '../components/StatsCard';
import {PlanChips, SubscriptionStatusPills} from '../components/SubscriptionBadges';
import {DEFAULT_SEAT_TYPE, formatDate, formatMonthlyTotals, formatSeats, monthlyTotals, seatNoun} from '../utils/subscriptions';
import {SubscriptionDetailModal} from '../components/SubscriptionDetailModal';
import {gql} from 'types/__generated__';

const SUBSCRIPTIONS_QUERY = gql(`
  query OrganisationSubscriptions {
    organisations {
      id
      name
      active
      numberOfUsers
      activeSubscription {
        id
        status
        isActive
        isTrial
        isCwayTrial
        autoRenewal
        currentPeriodEnd
        hasModulePricing
        isManaged
        paymentTermsDays
        entitlements {
          module
          tier
          quantity
          includedUsers
        }
        products {
          id
          name
          quantity
          module
          tier
          addon
          seatType
          price {
            amount
            currency
          }
        }
      }
      seatUsage(seatType: PROJECT) {
        usedSeats
        includedSeats
        purchasedSeats
        totalSeats
        seatsLeft
      }
    }
  }
`);

/** Only the fields this page actually selects — the period start belongs to the detail view. */
type RowSubscription = Omit<ActiveSubscription, 'currentPeriodStart'>;

interface OrgSubscriptionRow {
  id: string;
  name: string;
  active: boolean;
  numberOfUsers: number;
  activeSubscription: RowSubscription | null;
  seatUsage: SeatUsage | null;
}

type SubscriptionFilter = 'all' | 'module' | 'trial' | 'managed' | 'legacy' | 'none';

const FILTER_LABELS: Record<SubscriptionFilter, string> = {
  all: 'All',
  module: 'Module pricing',
  trial: 'Trialling',
  managed: 'Managed',
  legacy: 'Legacy',
  none: 'No subscription',
};

const PAGE_SIZE = 25;

function matchesFilter(org: OrgSubscriptionRow, filter: SubscriptionFilter): boolean {
  const sub = org.activeSubscription;
  const hasActive = !!sub && sub.isActive;
  switch (filter) {
    case 'all':
      return true;
    case 'module':
      return hasActive && sub!.hasModulePricing;
    case 'trial':
      return hasActive && sub!.isTrial;
    case 'managed':
      return hasActive && sub!.isManaged;
    case 'legacy':
      return hasActive && !sub!.hasModulePricing && !sub!.isTrial;
    case 'none':
      return !hasActive;
  }
}

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailOrg, setDetailOrg] = useState<{id: string; name: string} | null>(null);

  const searchTerm = searchParams.get('q') || '';
  const filterParam = searchParams.get('filter');
  // A stale or mistyped URL must not silently filter everything away.
  const filter: SubscriptionFilter = filterParam && filterParam in FILTER_LABELS ? (filterParam as SubscriptionFilter) : 'all';
  const currentPage = parseInt(searchParams.get('page') || '0', 10) || 0;

  const {loading, error, data, refetch} = useQuery<{organisations: OrgSubscriptionRow[]}>(SUBSCRIPTIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const organisations: OrgSubscriptionRow[] = useMemo(() => (data?.organisations || []).filter(Boolean), [data]);

  const filtered = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    return organisations
      .filter((org) => org.name?.toLowerCase().includes(needle) && matchesFilter(org, filter))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organisations, searchTerm, filter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = useMemo(() => filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE), [filtered, currentPage]);

  // One pass for every filter tab's count, instead of one pass per button per render.
  const filterCounts = useMemo(() => {
    const counts = {all: 0, module: 0, trial: 0, managed: 0, legacy: 0, none: 0} as Record<SubscriptionFilter, number>;
    for (const org of organisations) {
      for (const key of Object.keys(counts) as SubscriptionFilter[]) {
        if (matchesFilter(org, key)) counts[key] += 1;
      }
    }
    return counts;
  }, [organisations]);

  const stats = useMemo(() => {
    const active = organisations.filter((org) => org.activeSubscription?.isActive);
    const mrr = new Map<string, number>();
    for (const org of active) {
      const subscription = org.activeSubscription!;
      if (subscription.isTrial) continue;
      for (const [currency, amount] of monthlyTotals(subscription.products)) {
        mrr.set(currency, (mrr.get(currency) || 0) + amount);
      }
    }
    return {
      active: active.length,
      trialling: active.filter((org) => org.activeSubscription!.isTrial).length,
      managed: active.filter((org) => org.activeSubscription!.isManaged).length,
      legacy: active.filter((org) => !org.activeSubscription!.hasModulePricing && !org.activeSubscription!.isTrial).length,
      mrr,
    };
  }, [organisations]);

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading subscriptions</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Subscriptions</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Subscription and trial state across all organisations — the Cway staff sales surface
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatsCard label="Active Subscriptions" value={stats.active} valueColor="text-green-600" />
        <StatsCard label="Trialling" value={stats.trialling} valueColor="text-amber-600" />
        <StatsCard label="Managed" value={stats.managed} valueColor="text-purple-600" />
        <StatsCard label="Legacy" value={stats.legacy} />
        <StatsCard label="MRR (excl. trials)" value={formatMonthlyTotals(stats.mrr)} valueColor="text-blue-600" />
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search organisations by name..."
            value={searchTerm}
            onChange={(e) => updateParams({q: e.target.value || null, page: null})}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {(Object.keys(FILTER_LABELS) as SubscriptionFilter[]).map((key) => (
            <button
              key={key}
              onClick={() => updateParams({filter: key === 'all' ? null : key, page: null})}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {FILTER_LABELS[key]} ({filterCounts[key]})
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-x-auto animate-fadeIn">
        <table className="w-full text-left">
          <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-4 py-3 font-medium">Organisation</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">{seatNoun(DEFAULT_SEAT_TYPE, {capital: true, plural: true})}</th>
              <th className="px-4 py-3 font-medium">Period ends</th>
              <th className="px-4 py-3 font-medium text-right">Monthly</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {paginated.map((org) => {
              const sub = org.activeSubscription;
              const seatText = org.seatUsage ? formatSeats(org.seatUsage.usedSeats, org.seatUsage.totalSeats) : '—';
              const monthly = sub?.isActive && !sub.isTrial ? formatMonthlyTotals(monthlyTotals(sub.products)) : '—';
              return (
                <tr
                  key={org.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                  onClick={() => setDetailOrg({id: org.id, name: org.name})}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{org.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {org.numberOfUsers} users{!org.active && ' · inactive org'}
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-md">
                    <PlanChips subscription={sub} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <SubscriptionStatusPills subscription={sub} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{seatText}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {sub?.isActive ? formatDate(sub.currentPeriodEnd) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right whitespace-nowrap">{monthly}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/organisations/${org.id}`);
                      }}
                      title="Open organisation"
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                      <ExternalLink size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {paginated.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">No organisations match the current filter</div>
        )}
        {loading && organisations.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading…</div>}
      </div>

      {/* Results count and Pagination */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing {paginated.length} of {filtered.length} organisations
          {totalPages > 1 && ` - Page ${currentPage + 1} of ${totalPages}`}
        </div>
        {totalPages > 1 && (
          <div className="flex gap-2">
            <button
              onClick={() => updateParams({page: currentPage - 1 === 0 ? null : String(currentPage - 1)})}
              disabled={currentPage === 0}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </div>
            <button
              onClick={() => updateParams({page: String(currentPage + 1)})}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {detailOrg && (
        <SubscriptionDetailModal
          organisationId={detailOrg.id}
          organisationName={detailOrg.name}
          onClose={() => setDetailOrg(null)}
          onChanged={() => refetch()}
        />
      )}
    </div>
  );
}
